import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { constants } from '../../config';
import { CountryCode } from '../models/country_codes_response';
import { Product } from '../models/product_list_response';
import {
  fetchCountryCodesService,
  loginMerchantService,
  logoutService,
  sendOtpService,
  verifyLoginService,
  verifyOtpService,
} from '../services/auth.service';

export const fetchCountryCodes = createAsyncThunk('auth/fetchCountryCodes', async (payload, thunkAPI) => {
  const countryCodesResponse = await fetchCountryCodesService();
  if (countryCodesResponse.isSuccessful) {
    return countryCodesResponse.data;
  } else {
    thunkAPI.rejectWithValue('Unable to fetch country codes.');
    throw new Error('Unable to fetch country codes.');
  }
});

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (
    payload: { countryCode: string; contactNumber: string; storeSlug: string; merchantStoreId: string },
    thunkAPI,
  ) => {
    const sendOtpResponse = await sendOtpService({
      countryCode: payload.countryCode,
      contactNumber: payload.contactNumber,
      storeSlug: payload.storeSlug,
      merchantStoreId: payload.merchantStoreId,
    });
    if (sendOtpResponse.isSuccessful) {
      return sendOtpResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to send OTP.');
      throw new Error('Unable to send OTP.');
    }
  },
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async (
    payload: {
      phoneNumber: string;
      userId: string;
      storeSlug: string;
      verificationCode: number;
      merchantStoreId: string;
    },
    thunkAPI,
  ) => {
    const verifyOtpResponse = await verifyOtpService({
      phoneNumber: payload.phoneNumber,
      userId: payload.userId,
      storeSlug: payload.storeSlug,
      verificationCode: payload.verificationCode,
      merchantStoreId: payload.merchantStoreId,
    });
    if (verifyOtpResponse.isSuccessful) {
      thunkAPI.dispatch(saveTokenToStorage({ ...verifyOtpResponse.data.data, storeSlug: payload.storeSlug }));
      return verifyOtpResponse.data;
    } else {
      thunkAPI.rejectWithValue(verifyOtpResponse.data.message);
      throw new Error(verifyOtpResponse.data.message);
    }
  },
);

export const loginMerchant = createAsyncThunk(
  'auth/loginMerchant',
  async (
    payload: {
      token: string;
      merchantStoreName: string;
      merchantStoreId: string;
      storeSlug: string;
    },
    thunkAPI,
  ) => {
    const merchantLoginResponse = await loginMerchantService({
      token: payload.token,
      merchantStoreName: payload.merchantStoreName,
      merchantStoreId: payload?.merchantStoreId,
    });
    if (merchantLoginResponse.isSuccessful) {
      thunkAPI.dispatch(saveTokenToStorage({ ...merchantLoginResponse.data.data, storeSlug: payload.storeSlug }));
      return merchantLoginResponse.data;
    } else {
      thunkAPI.rejectWithValue(merchantLoginResponse.data.message);
      throw new Error(merchantLoginResponse.data.message);
    }
  },
);

export const verifyLogin = createAsyncThunk('auth/verifyLogin', async (payload: { storeSlug: string }, thunkAPI) => {
  const verifyLoginResponse = await verifyLoginService({ storeSlug: payload.storeSlug });
  if (verifyLoginResponse.isLoggedIn === true) {
    thunkAPI.dispatch(saveTokenToStorage({ ...verifyLoginResponse, storeSlug: payload.storeSlug }));
    return verifyLoginResponse;
  } else {
    thunkAPI.dispatch(deleteTokenFromStorage({ ...verifyLoginResponse, storeSlug: payload.storeSlug }));
    return verifyLoginResponse;
  }
});

export const logout = createAsyncThunk('auth/logout', async (payload: { storeSlug: string }, thunkAPI) => {
  const logoutResponse = await logoutService();
  if (logoutResponse.isLoggedIn === false) {
    thunkAPI.dispatch(deleteTokenFromStorage({ storeSlug: payload.storeSlug }));
    return logoutResponse;
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isLoading: false,
    isLoggedIn: false,
    isOtpSent: false,
    phoneNumber: '',
    userId: '',
    shopperAccountId: '',
    countryCodes: <CountryCode[]>[],
  },
  reducers: {
    saveTokenToStorage: (state, action) => {
      localStorage.setItem(constants.USER_TOKEN_KEY + action.payload.storeSlug, action.payload.authToken);
      localStorage.setItem(constants.USER_SHOPPER_ID_KEY + action.payload.storeSlug, action.payload.shopperAccountId);
      localStorage.setItem(constants.USER_ID_KEY + action.payload.storeSlug, action.payload.userId);
      localStorage.setItem(constants.USER_PHONE_KEY + action.payload.storeSlug, action.payload.phoneNumber);
      localStorage.setItem(constants.USER_NAME + action.payload.storeSlug, action.payload.shopperName);
      localStorage.setItem(constants.USER_ADDR_L1 + action.payload.storeSlug, action.payload.address);
    },
    deleteTokenFromStorage: (state, action) => {
      localStorage.removeItem(constants.USER_TOKEN_KEY + action.payload.storeSlug);
      localStorage.removeItem(constants.USER_SHOPPER_ID_KEY + action.payload.storeSlug);
      localStorage.removeItem(constants.USER_ID_KEY + action.payload.storeSlug);
      localStorage.removeItem(constants.USER_PHONE_KEY + action.payload.storeSlug);
      localStorage.removeItem(constants.USER_NAME + action.payload.storeSlug);
      localStorage.removeItem(constants.USER_ADDR_L1 + action.payload.storeSlug);
    },
  },
  extraReducers: {
    [fetchCountryCodes.pending.type]: (state, action) => {
      state.countryCodes = [];
    },
    [fetchCountryCodes.fulfilled.type]: (state, action) => {
      state.countryCodes = action.payload.data;
    },
    [fetchCountryCodes.rejected.type]: (state, action) => {
      toast.error(action.error.message, {
        position: 'bottom-center',
      });
    },
    [sendOtp.pending.type]: (state, action) => {
      state.isLoading = true;
      state.isOtpSent = false;
      state.userId = '';
      state.phoneNumber = '';
    },
    [sendOtp.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      state.isOtpSent = true;
      state.userId = action.payload.data.userId;
      state.phoneNumber = action.payload.data.phoneNumber;
      toast.success(action.payload.message, {
        position: 'bottom-center',
      });
    },
    [sendOtp.rejected.type]: (state, action) => {
      state.isLoading = false;
      state.isOtpSent = false;
      toast.error(action.error.message, {
        position: 'bottom-center',
      });
    },
    [verifyOtp.pending.type]: (state, action) => {
      state.isLoading = true;
    },
    [verifyOtp.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = true;
      state.userId = action.payload.data.userId;
      state.shopperAccountId = action.payload.shopperAccountId;
      state.phoneNumber = action.payload.data.phoneNumber;
      toast.success(action.payload.message, {
        position: 'bottom-center',
      });
    },
    [verifyOtp.rejected.type]: (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = false;
      state.isOtpSent = false;
      toast.error(action.error.message, {
        position: 'bottom-center',
      });
    },
    [loginMerchant.pending.type]: (state, action) => {
      state.isLoading = true;
    },
    [loginMerchant.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = true;
      state.userId = action.payload.data.userId;
      state.shopperAccountId = action.payload.shopperAccountId;
      state.phoneNumber = action.payload.data.phoneNumber;
    },
    [loginMerchant.rejected.type]: (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = false;
      state.isOtpSent = false;
      toast.error(action.error.message, {
        position: 'bottom-center',
      });
    },
    [verifyLogin.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = action.payload.isLoggedIn;
      state.userId = action.payload.userId;
      state.shopperAccountId = action.payload.shopperAccountId;
      state.phoneNumber = action.payload.phoneNumber;
    },
    [logout.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      toast.success('Logged out successfully', {
        position: 'bottom-center',
      });
      window.location.reload();
    },
  },
});

export const { saveTokenToStorage, deleteTokenFromStorage } = authSlice.actions;

export default authSlice.reducer;
